import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, FileText, Video, HelpCircle, ChevronRight, 
  Search, Play, ExternalLink, ArrowLeft, Menu, X,
  Building2, Users, Calendar, Settings, CreditCard, Key,
  Layout, Palette, Code, CheckCircle2, AlertTriangle, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const LOGO_URL = "/logo.png";

// Contenu détaillé des articles
const articleContents = {
  1: {
    title: 'Créer votre compte entreprise',
    category: 'Démarrage rapide',
    duration: '5 min',
    content: `
## 1. Demander une licence d'essai

Pour commencer avec ResaWork, vous avez besoin d'une licence. Deux options s'offrent à vous :

- **Essai gratuit de 15 jours** : Cliquez sur "Démarrer gratuitement" sur la page d'accueil
- **Licence payante** : Contactez notre équipe commerciale

## 2. Créer votre compte

Une fois votre clé de licence reçue par email :

1. Accédez à la page d'inscription
2. Renseignez les informations de votre entreprise :
   - Nom de l'entreprise
   - Email professionnel
   - Mot de passe sécurisé
   - Numéro de téléphone
   - Adresse
3. Entrez votre clé de licence
4. Cliquez sur "Créer mon compte"

## 3. Première connexion

Après l'inscription, vous serez redirigé vers votre tableau de bord. Nous vous recommandons de :

1. **Compléter votre profil** : Ajoutez votre logo et description
2. **Configurer vos services** : Définissez vos prestations et tarifs
3. **Ajouter vos collaborateurs** : Invitez votre équipe
4. **Créer votre premier widget** : Commencez à recevoir des réservations

> **Astuce** : Suivez l'assistant d'onboarding qui vous guidera étape par étape !
    `
  },
  2: {
    title: 'Configurer vos services',
    category: 'Démarrage rapide',
    duration: '10 min',
    content: `
## Qu'est-ce qu'un service ?

Un service représente une prestation que vous proposez à vos clients. Par exemple :
- Entretien de jardin
- Tonte de pelouse
- Taille de haies
- Nettoyage

## Créer un service

1. Accédez à **Mon activité > Services**
2. Cliquez sur **"Ajouter un service"**
3. Remplissez les informations :

### Informations de base
- **Nom du service** : Ex. "Entretien de jardin"
- **Description** : Détaillez ce que comprend la prestation
- **Durée** : Temps estimé (en heures)

### Tarification

Trois modes de tarification sont disponibles :

| Mode | Description | Exemple |
|------|-------------|---------|
| **Taux horaire** | Prix calculé selon la durée | 35€/h |
| **Prix fixe** | Montant forfaitaire | 150€ |
| **Informatif** | Sans prix affiché | Sur devis |

### Options avancées
- **Éligible au crédit d'impôt** : Cochez si le service donne droit à 50% de crédit d'impôt
- **Image** : Ajoutez une photo représentative
- **Collaborateurs assignés** : Sélectionnez qui peut réaliser ce service

## Modifier ou supprimer un service

- Cliquez sur l'icône **crayon** pour modifier
- Cliquez sur l'icône **corbeille** pour supprimer

> **Important** : Un service utilisé dans des réservations existantes ne peut pas être supprimé.
    `
  },
  3: {
    title: 'Ajouter vos collaborateurs',
    category: 'Démarrage rapide',
    duration: '5 min',
    content: `
## Pourquoi ajouter des collaborateurs ?

Les collaborateurs sont les membres de votre équipe qui :
- Réalisent les interventions
- Ont leur propre planning
- Peuvent se connecter à leur espace dédié

## Créer un collaborateur

1. Allez dans **Mon activité > Collaborateurs**
2. Cliquez sur **"Ajouter un collaborateur"**
3. Remplissez :
   - **Prénom et Nom**
   - **Email** (servira d'identifiant de connexion)
   - **Téléphone**
   - **Couleur** (pour le distinguer dans le planning)
   - **Services assignés** (quelles prestations il peut effectuer)

## Accès collaborateur

Chaque collaborateur reçoit un email avec ses identifiants pour se connecter à son espace :
- Voir son planning
- Consulter les détails des interventions
- Marquer les interventions comme terminées

## Gérer les collaborateurs

### Modifier les informations
Cliquez sur le collaborateur pour ouvrir sa fiche et modifier ses informations.

### Envoyer un lien de réinitialisation
Si un collaborateur a oublié son mot de passe, cliquez sur l'icône **clé** pour lui envoyer un lien de réinitialisation.

### Ajouter un avatar
Uploadez une photo pour personnaliser le profil du collaborateur.

> **Conseil** : Assignez des couleurs distinctes à chaque collaborateur pour une meilleure lisibilité du planning !
    `
  },
  4: {
    title: 'Personnaliser votre widget',
    category: 'Démarrage rapide',
    duration: '8 min',
    content: `
## Les widgets ResaWork

ResaWork propose deux types de widgets de réservation :

### Widget Ponctuel
Pour les réservations **one-shot** : le client choisit un créneau et réserve une seule intervention.

### Widget Abonnement
Pour les réservations **récurrentes** : le client s'abonne à un service régulier (hebdomadaire, bi-hebdomadaire, mensuel).

## Créer un widget

1. Accédez à **Widget > Mes Widgets**
2. Cliquez sur **"Créer un widget"**
3. Choisissez le type (Ponctuel ou Abonnement)

## Personnalisation

### Onglet Général
- **Nom du widget** : Pour vous repérer dans votre liste
- **Services proposés** : Sélectionnez les services disponibles
- **Message de bienvenue** : Texte affiché au client

### Onglet Tarification
- **Mode de calcul** : Taux horaire global ou prix par service
- **Affichage crédit d'impôt** : Montrer le prix après réduction

### Onglet Champs
Personnalisez les informations demandées au client :
- Prénom, Nom (obligatoires)
- Email, Téléphone
- Adresse
- Champs personnalisés

### Onglet Style
- **Couleur principale** : Harmonisez avec votre charte graphique
- **Bouton arrondi** : Activez pour un style moderne

### Onglet CGV
Ajoutez vos conditions générales de vente.

## Intégrer le widget

Une fois configuré, copiez le **lien direct** ou le **code iframe** pour l'intégrer sur votre site web.

\`\`\`html
<iframe src="https://resawork.fr/widget/votre-slug" 
        width="100%" height="800" frameborder="0">
</iframe>
\`\`\`
    `
  },
  5: {
    title: 'Créer un widget d\'abonnement',
    category: 'Widgets',
    duration: '10 min',
    content: `
## Widget Abonnement : pour qui ?

Le widget abonnement est idéal pour :
- Les services récurrents (entretien régulier, ménage, etc.)
- Les forfaits mensuels
- Les clients fidèles

## Étapes de création

### 1. Créer le widget
- **Widget > Mes Widgets > Créer un widget**
- Sélectionnez **"Abonnement"**

### 2. Configurer les services
Choisissez les services éligibles à l'abonnement.

### 3. Définir les fréquences
Les clients pourront choisir parmi :
- **Hebdomadaire** : Même jour chaque semaine
- **Bi-hebdomadaire** : Une semaine sur deux
- **Mensuel** : Une fois par mois
- **Personnalisé** : Intervalle défini par le client

### 4. Personnaliser l'apparence
- Logo de votre entreprise
- Couleurs de votre charte graphique
- Message personnalisé

## Validation des demandes

Quand un client fait une demande d'abonnement :

1. Vous recevez une **notification**
2. La demande apparaît dans **Réservations > Demandes Abo.**
3. Vous pouvez **accepter** ou **refuser** la demande
4. Le client est notifié par email

## Gestion des abonnements actifs

Les abonnements validés génèrent automatiquement les créneaux dans votre planning selon la fréquence choisie.
    `
  },
  6: {
    title: 'Créer un widget ponctuel',
    category: 'Widgets',
    duration: '10 min',
    content: `
## Widget Ponctuel : pour qui ?

Le widget ponctuel convient pour :
- Les interventions uniques
- Les devis sur demande
- Les nouveaux clients qui testent vos services

## Configuration pas à pas

### 1. Informations de base
- **Nom** : Identifiant interne (ex: "Widget site web")
- **Services** : Sélectionnez un ou plusieurs services

### 2. Tarification

| Option | Comportement |
|--------|--------------|
| Taux horaire global | Un seul tarif pour toutes les prestations |
| Prix par service | Chaque service a son propre prix |
| Sélection multiple | Le client peut choisir plusieurs services |

### 3. Créneaux disponibles

Le widget affiche automatiquement vos disponibilités basées sur :
- Le planning de vos collaborateurs
- Les réservations existantes
- Vos jours d'ouverture

### 4. Étapes du formulaire

Personnalisez le parcours client :
1. **Choix du service**
2. **Sélection du créneau**
3. **Informations personnelles**
4. **Récapitulatif et validation**

## Réception des réservations

Les nouvelles réservations :
- Apparaissent dans **Réservations > Ponctuelles**
- Sont visibles dans le **Planning**
- Déclenchent une **notification** (icône cloche)
- Envoient un **email de confirmation** au client
    `
  },
  7: {
    title: 'Personnaliser les couleurs et champs',
    category: 'Widgets',
    duration: '8 min',
    content: `
## Personnalisation visuelle

### Couleur principale
La couleur principale est utilisée pour :
- Les boutons
- Les éléments de sélection
- Les liens

**Comment la changer :**
1. Ouvrez votre widget en édition
2. Allez dans l'onglet **Style**
3. Cliquez sur le sélecteur de couleur
4. Choisissez votre couleur (code HEX supporté)

### Conseils de design
- Utilisez la couleur de votre logo
- Assurez un bon contraste avec le blanc
- Testez sur mobile et desktop

## Champs du formulaire

### Champs par défaut
- Prénom ✓ (obligatoire)
- Nom ✓ (obligatoire)
- Email ✓ (obligatoire)
- Téléphone
- Adresse

### Ajouter un champ personnalisé
1. Onglet **Champs**
2. Cliquez sur **"Ajouter un champ"**
3. Configurez :
   - **Libellé** : Texte affiché
   - **Type** : Texte, Nombre, Date, Case à cocher
   - **Obligatoire** : Oui/Non
   - **Placeholder** : Texte d'exemple

### Organiser les champs
Utilisez le drag & drop pour réorganiser l'ordre des champs dans chaque étape.

## Prévisualisation
Cliquez sur **"Aperçu"** pour voir le rendu final avant de publier.
    `
  },
  8: {
    title: 'Intégrer le widget sur votre site',
    category: 'Widgets',
    duration: '5 min',
    content: `
## Méthodes d'intégration

### 1. Lien direct
Partagez simplement l'URL de votre widget :
\`\`\`
https://votre-domaine.com/reservation/votre-slug
\`\`\`

### 2. Bouton de réservation
Ajoutez un bouton qui ouvre le widget :
\`\`\`html
<a href="https://votre-domaine.com/reservation/votre-slug" 
   class="btn-reservation">
   Réserver maintenant
</a>
\`\`\`

### 3. Iframe (intégration complète)
Intégrez le widget directement dans votre page :
\`\`\`html
<iframe 
  src="https://votre-domaine.com/reservation/votre-slug"
  width="100%" 
  height="800" 
  frameborder="0"
  style="border: none; border-radius: 12px;">
</iframe>
\`\`\`

## Intégration WordPress

### Avec Elementor
1. Ajoutez un widget **HTML personnalisé**
2. Collez le code iframe
3. Ajustez la hauteur si nécessaire

### Avec l'éditeur classique
1. Passez en mode **Texte** (pas Visuel)
2. Collez le code iframe
3. Publiez la page

## Conseils
- **Hauteur recommandée** : 800px minimum
- **Largeur** : 100% pour un affichage responsive
- **HTTPS** : Assurez-vous que votre site est en HTTPS
    `
  },
  9: {
    title: 'Vue d\'ensemble du planning',
    category: 'Planning',
    duration: '8 min',
    content: `
## Accéder au planning

Le planning est accessible depuis **Réservations > Planning** dans le menu latéral.

## Vues disponibles

### Vue Jour
Affiche les créneaux d'une journée pour tous les collaborateurs côte à côte.

### Vue Semaine
Vue la plus utilisée : aperçu de la semaine complète.

### Vue Mois
Idéale pour une vision globale et la planification à long terme.

### Vue Agenda
Liste chronologique des interventions à venir.

## Code couleur

| Couleur | Signification |
|---------|---------------|
| 🟢 Vert | Disponible |
| 🔵 Bleu | Réservé |
| 🟡 Orange | En attente de confirmation |
| 🔴 Rouge | Indisponible |

## Filtres

- **Par collaborateur** : Affichez le planning d'un seul collaborateur
- **Par service** : Filtrez par type de prestation
- **Plage de dates** : Navigation rapide

## Actions rapides

- **Clic sur un créneau vide** : Créer une réservation
- **Clic sur une réservation** : Voir les détails
- **Drag & drop** : Déplacer une intervention (si activé)

## Export PDF

Cliquez sur l'icône **téléchargement** pour exporter le planning de la semaine en PDF.
    `
  },
  10: {
    title: 'Ajouter des disponibilités',
    category: 'Planning',
    duration: '5 min',
    content: `
## Gestion des disponibilités

Les disponibilités déterminent quand vos collaborateurs peuvent recevoir des réservations.

## Définir les horaires de travail

### Par collaborateur
1. Allez dans **Mon activité > Collaborateurs**
2. Sélectionnez un collaborateur
3. Configurez ses **horaires de travail** :
   - Jours travaillés
   - Heures de début et fin
   - Pause déjeuner

### Horaires globaux
Définissez des horaires par défaut pour toute l'équipe dans **Paramètres > Général**.

## Ajouter une disponibilité exceptionnelle

Pour ouvrir un créneau en dehors des horaires habituels :
1. Cliquez sur le créneau souhaité dans le planning
2. Sélectionnez **"Rendre disponible"**
3. Confirmez

## Bloquer une période

Pour bloquer un créneau (congés, indisponibilité) :
1. Cliquez sur le créneau concerné
2. Sélectionnez **"Rendre indisponible"**
3. Optionnel : ajoutez une raison

## Récurrence

Vous pouvez créer des disponibilités récurrentes :
- Chaque semaine le même jour
- Tous les 15 jours
- Chaque mois
    `
  },
  11: {
    title: 'Gérer les réservations',
    category: 'Planning',
    duration: '10 min',
    content: `
## Types de réservations

### Réservations ponctuelles
- Interventions uniques
- Accessibles dans **Réservations > Ponctuelles**

### Demandes d'abonnement
- Demandes de services récurrents
- À valider dans **Réservations > Demandes Abo.**

## Cycle de vie d'une réservation

\`\`\`
Nouvelle → Confirmée → En cours → Terminée
                ↓
            Annulée
\`\`\`

## Actions disponibles

### Sur une réservation ponctuelle
- **Voir les détails** : Informations client et intervention
- **Modifier** : Changer la date, l'heure ou le collaborateur
- **Annuler** : Annuler la réservation
- **Terminer** : Marquer comme terminée

### Sur une demande d'abonnement
- **Accepter** : Valide la demande et crée les créneaux récurrents
- **Refuser** : Refuse la demande avec un motif optionnel

## Notifications

À chaque changement de statut, le client reçoit un email automatique :
- Confirmation de réservation
- Rappel avant intervention
- Notification d'annulation

## Créer une réservation manuelle

1. Cliquez sur un créneau vide dans le planning
2. Sélectionnez **"Créer une réservation"**
3. Choisissez un client existant ou créez-en un nouveau
4. Remplissez les détails de l'intervention
5. Validez
    `
  },
  12: {
    title: 'Exporter en PDF',
    category: 'Planning',
    duration: '3 min',
    content: `
## Exporter le planning

### Export hebdomadaire
1. Naviguez vers la **semaine souhaitée**
2. Cliquez sur l'icône **téléchargement** (en haut à droite)
3. Le PDF est généré automatiquement

### Contenu du PDF
- Logo de votre entreprise
- Période concernée
- Planning de chaque collaborateur
- Détails des interventions

## Partager le planning

Le PDF exporté peut être :
- Envoyé par email à vos collaborateurs
- Imprimé et affiché
- Archivé pour vos dossiers

## Cas d'usage
- **Réunion d'équipe** : Distribuer le planning de la semaine
- **Suivi administratif** : Archiver les interventions réalisées
- **Communication client** : Confirmer un planning d'interventions
    `
  },
  13: {
    title: 'Créer un compte client',
    category: 'Clients',
    duration: '5 min',
    content: `
## Deux façons de créer un client

### 1. Via le widget (automatique)
Quand un nouveau client fait une réservation, son compte est créé automatiquement.

### 2. Via le back-office (manuel)
1. Allez dans **Clients > CRM Clients**
2. Cliquez sur **"Créer un client"**
3. Remplissez les informations :
   - Prénom et nom
   - Email
   - Téléphone
   - Adresse complète
4. Optionnel : cochez **"Créer un abonnement"** pour un client récurrent

## Fiche client

Chaque client dispose d'une fiche détaillée avec :
- **Coordonnées** : Informations de contact
- **Historique** : Toutes les interventions passées
- **Crédits** : Solde de crédits prépayés
- **Documents** : Factures et devis
- **Échanges** : Notes et communications

## Accès client

Les clients peuvent se connecter à leur espace personnel pour :
- Voir leurs prochaines interventions
- Consulter leur historique
- Gérer leurs crédits
- Réserver une nouvelle intervention
    `
  },
  14: {
    title: 'Gérer les crédits',
    category: 'Clients',
    duration: '5 min',
    content: `
## Système de crédits

Les crédits permettent aux clients de :
- Prépayer des heures de service
- Bénéficier d'un suivi de leur solde
- Faciliter le paiement des interventions

## Ajouter des crédits

1. Ouvrez la fiche du client
2. Cliquez sur **"Ajouter des crédits"**
3. Entrez :
   - **Nombre d'heures** : Quantité de crédits
   - **Montant payé** : Prix facturé au client
4. Validez

## Décompte automatique

À chaque intervention terminée, les crédits sont automatiquement déduits du solde client selon la durée de l'intervention.

## Suivi des crédits

### Côté entreprise
Visualisez le solde de chaque client dans :
- La liste des clients (colonne Crédits)
- La fiche client détaillée

### Côté client
Le client voit son solde dans son espace personnel et reçoit une notification quand son solde est bas.

## Crédit d'impôt

Si vos services sont éligibles au crédit d'impôt (50%), les clients voient :
- Le prix avant réduction
- Le prix après crédit d'impôt
- L'économie réalisée
    `
  },
  15: {
    title: 'Valider une demande d\'abonnement',
    category: 'Clients',
    duration: '8 min',
    content: `
## Recevoir une demande

Quand un client fait une demande d'abonnement :
1. Vous recevez une **notification** (cloche dans le header)
2. Un email est envoyé à votre adresse
3. La demande apparaît dans **Réservations > Demandes Abo.**

## Examiner la demande

Cliquez sur la demande pour voir :
- **Client** : Coordonnées et historique
- **Service** : Prestation demandée
- **Fréquence** : Hebdo, bi-hebdo, mensuel
- **Jour préféré** : Créneau souhaité
- **Notes** : Commentaires du client

## Accepter la demande

1. Cliquez sur **"Accepter"**
2. Sélectionnez le **collaborateur** assigné
3. Confirmez le **créneau** (ou proposez une alternative)
4. Validez

**Résultat :**
- Le client reçoit un email de confirmation
- Les créneaux récurrents sont créés automatiquement dans le planning
- L'abonnement devient actif

## Refuser la demande

1. Cliquez sur **"Refuser"**
2. Optionnel : ajoutez un **motif** (créneau indisponible, secteur non couvert, etc.)
3. Confirmez

Le client reçoit un email l'informant du refus.
    `
  },
  16: {
    title: 'Espace client: guide utilisateur',
    category: 'Clients',
    duration: '10 min',
    content: `
## Accéder à l'espace client

Les clients se connectent via :
- **URL générique** : /login (onglet Client)
- **URL personnalisée** : /connexion/nom-entreprise

## Fonctionnalités disponibles

### Dashboard
- Prochaines interventions
- Solde de crédits
- Accès rapide à la réservation

### Mes réservations
- Liste des interventions passées et à venir
- Détails de chaque intervention
- Possibilité d'annuler (selon vos paramètres)

### Nouvelle réservation
- Choix du service
- Sélection du créneau disponible
- Confirmation et paiement

### Mon compte
- Modification des informations personnelles
- Historique des paiements
- Gestion du mot de passe

## Personnalisation

L'espace client reprend :
- Le **logo** de votre entreprise
- Les **couleurs** de votre widget
- Vos **coordonnées** de contact

## Support client

Les clients peuvent :
- Vous contacter via le chat ou email
- Ouvrir un ticket de support
- Laisser un avis après une intervention
    `
  }
};

const categories = [
  {
    id: 'getting-started',
    title: 'Démarrage rapide',
    icon: Play,
    description: 'Premiers pas avec ResaWork',
    articles: [
      { id: 1, title: 'Créer votre compte entreprise', duration: '5 min', type: 'guide' },
      { id: 2, title: 'Configurer vos services', duration: '10 min', type: 'guide' },
      { id: 3, title: 'Ajouter vos collaborateurs', duration: '5 min', type: 'guide' },
      { id: 4, title: 'Personnaliser votre widget', duration: '8 min', type: 'guide' },
    ]
  },
  {
    id: 'widgets',
    title: 'Widgets de réservation',
    icon: Layout,
    description: 'Configurez vos formulaires de réservation',
    articles: [
      { id: 5, title: 'Créer un widget d\'abonnement', duration: '10 min', type: 'guide' },
      { id: 6, title: 'Créer un widget ponctuel', duration: '10 min', type: 'guide' },
      { id: 7, title: 'Personnaliser les couleurs et champs', duration: '8 min', type: 'guide' },
      { id: 8, title: 'Intégrer le widget sur votre site', duration: '5 min', type: 'guide' },
    ]
  },
  {
    id: 'planning',
    title: 'Gestion du planning',
    icon: Calendar,
    description: 'Gérez vos disponibilités et réservations',
    articles: [
      { id: 9, title: 'Vue d\'ensemble du planning', duration: '8 min', type: 'guide' },
      { id: 10, title: 'Ajouter des disponibilités', duration: '5 min', type: 'guide' },
      { id: 11, title: 'Gérer les réservations', duration: '10 min', type: 'guide' },
      { id: 12, title: 'Exporter en PDF', duration: '3 min', type: 'guide' },
    ]
  },
  {
    id: 'clients',
    title: 'Gestion des clients',
    icon: Users,
    description: 'Gérez vos clients et abonnements',
    articles: [
      { id: 13, title: 'Créer un compte client', duration: '5 min', type: 'guide' },
      { id: 14, title: 'Gérer les crédits', duration: '5 min', type: 'guide' },
      { id: 15, title: 'Valider une demande d\'abonnement', duration: '8 min', type: 'guide' },
      { id: 16, title: 'Espace client: guide utilisateur', duration: '10 min', type: 'guide' },
    ]
  },
];

const faqs = [
  { 
    q: 'Comment obtenir une clé de licence ?', 
    a: 'Cliquez sur "Démarrer gratuitement" sur la page d\'accueil pour obtenir un essai gratuit de 15 jours. Pour une licence payante, choisissez votre formule (mensuelle ou annuelle) et procédez au paiement sécurisé.' 
  },
  { 
    q: 'Puis-je modifier mes services après création ?', 
    a: 'Oui, vous pouvez modifier le nom, la description, la durée et le tarif de vos services à tout moment. Seuls les services liés à des réservations existantes ne peuvent pas être supprimés.' 
  },
  { 
    q: 'Comment intégrer le widget sur WordPress ?', 
    a: 'Utilisez le code iframe généré depuis la page de configuration de votre widget. Collez-le dans un bloc HTML personnalisé (Elementor) ou en mode Texte de l\'éditeur classique.' 
  },
  { 
    q: 'Les clients peuvent-ils payer en ligne ?', 
    a: 'Actuellement, les clients peuvent prépayer des crédits que vous enregistrez manuellement. L\'intégration du paiement en ligne Stripe est disponible pour les abonnements entreprise.' 
  },
  { 
    q: 'Comment mes clients accèdent-ils à leur espace ?', 
    a: 'Vos clients peuvent se connecter via /login (onglet Client) ou via votre URL personnalisée /connexion/nom-entreprise. Leurs identifiants sont créés automatiquement lors de leur première réservation.' 
  },
  { 
    q: 'Le crédit d\'impôt est-il calculé automatiquement ?', 
    a: 'Oui, si vous cochez "Éligible au crédit d\'impôt" sur un service, le prix après réduction (50%) est affiché automatiquement aux clients dans le widget et leur espace.' 
  },
  { 
    q: 'Comment annuler un abonnement client ?', 
    a: 'Accédez à la fiche client, puis à l\'onglet "Abonnement". Cliquez sur "Suspendre" pour mettre en pause ou "Annuler" pour mettre fin définitivement à l\'abonnement.' 
  },
  { 
    q: 'Puis-je avoir plusieurs widgets ?', 
    a: 'Oui, vous pouvez créer autant de widgets que nécessaire. C\'est utile pour avoir des widgets différents par service ou par canal (site web, réseaux sociaux, etc.).' 
  },
];

export default function DocsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Filter articles based on search
  const filteredCategories = categories.map(category => ({
    ...category,
    articles: category.articles.filter(article => 
      article.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.articles.length > 0);

  // Render markdown-like content
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let inCodeBlock = false;
    let codeContent = [];
    let inTable = false;
    let tableRows = [];

    lines.forEach((line, index) => {
      // Code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={index} className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4 text-sm">
              <code>{codeContent.join('\n')}</code>
            </pre>
          );
          codeContent = [];
        }
        inCodeBlock = !inCodeBlock;
        return;
      }
      if (inCodeBlock) {
        codeContent.push(line);
        return;
      }

      // Tables
      if (line.startsWith('|')) {
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        if (!line.includes('---')) {
          tableRows.push(line.split('|').filter(cell => cell.trim()).map(cell => cell.trim()));
        }
        return;
      } else if (inTable) {
        elements.push(
          <div key={index} className="overflow-x-auto my-4">
            <table className="min-w-full border border-border rounded-lg">
              <thead className="bg-muted">
                <tr>
                  {tableRows[0]?.map((cell, i) => (
                    <th key={i} className="px-4 py-2 text-left text-sm font-medium">{cell}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.slice(1).map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-t border-border">
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-2 text-sm">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        inTable = false;
        tableRows = [];
      }

      // Headers
      if (line.startsWith('## ')) {
        elements.push(<h2 key={index} className="text-xl font-bold mt-8 mb-4">{line.replace('## ', '')}</h2>);
      } else if (line.startsWith('### ')) {
        elements.push(<h3 key={index} className="text-lg font-semibold mt-6 mb-3">{line.replace('### ', '')}</h3>);
      }
      // Blockquotes
      else if (line.startsWith('> ')) {
        const text = line.replace('> ', '');
        const isWarning = text.includes('Important') || text.includes('Attention');
        const isTip = text.includes('Astuce') || text.includes('Conseil');
        elements.push(
          <div key={index} className={`p-4 rounded-lg my-4 flex items-start gap-3 ${
            isWarning ? 'bg-orange-50 border border-orange-200' : 
            isTip ? 'bg-green-50 border border-green-200' : 
            'bg-blue-50 border border-blue-200'
          }`}>
            {isWarning ? <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" /> :
             isTip ? <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" /> :
             <Info className="w-5 h-5 text-blue-600 mt-0.5" />}
            <p className={`text-sm ${
              isWarning ? 'text-orange-800' : 
              isTip ? 'text-green-800' : 
              'text-blue-800'
            }`}>{text.replace('**Astuce** : ', '').replace('**Important** : ', '').replace('**Conseil** : ', '')}</p>
          </div>
        );
      }
      // List items
      else if (line.startsWith('- ')) {
        elements.push(
          <li key={index} className="ml-4 text-muted-foreground flex items-start gap-2">
            <span className="text-primary mt-1.5">•</span>
            <span>{line.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').split('<strong>').map((part, i) => 
              i % 2 === 0 ? part.replace('</strong>', '') : <strong key={i}>{part.replace('</strong>', '')}</strong>
            )}</span>
          </li>
        );
      }
      // Numbered lists
      else if (/^\d+\.\s/.test(line)) {
        const text = line.replace(/^\d+\.\s/, '');
        elements.push(
          <li key={index} className="ml-4 text-muted-foreground list-decimal list-inside">
            {text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').split('<strong>').map((part, i) => 
              i % 2 === 0 ? part.replace('</strong>', '') : <strong key={i}>{part.replace('</strong>', '')}</strong>
            )}
          </li>
        );
      }
      // Paragraphs
      else if (line.trim()) {
        elements.push(
          <p key={index} className="text-muted-foreground my-2">
            {line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').split('<strong>').map((part, i) => 
              i % 2 === 0 ? part.replace('</strong>', '') : <strong key={i} className="text-foreground">{part.replace('</strong>', '')}</strong>
            )}
          </p>
        );
      }
    });

    return elements;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="ResaWork" className="h-16" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/docs" className="text-sm font-medium text-primary">Documentation</Link>
              <Link to="/changelog" className="text-sm font-medium text-muted-foreground hover:text-foreground">Changelog</Link>
              <Link to="/tickets" className="text-sm font-medium text-muted-foreground hover:text-foreground">Support</Link>
            </nav>
            
            <div className="hidden md:flex items-center gap-3">
              <Button variant="ghost" asChild>
                <Link to="/login">Connexion</Link>
              </Button>
              <Button asChild className="rounded-full">
                <Link to="/onboarding">Démarrer</Link>
              </Button>
            </div>
            
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-background pt-16 md:hidden">
          <nav className="flex flex-col p-4 gap-4">
            <Link to="/docs" className="text-lg font-medium text-primary" onClick={() => setMobileMenuOpen(false)}>Documentation</Link>
            <Link to="/changelog" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Changelog</Link>
            <Link to="/tickets" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Support</Link>
            <hr className="my-2" />
            <Link to="/login" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Connexion</Link>
            <Button asChild className="rounded-full mt-2">
              <Link to="/onboarding" onClick={() => setMobileMenuOpen(false)}>Démarrer</Link>
            </Button>
          </nav>
        </div>
      )}

      {/* Article View */}
      {selectedArticle ? (
        <div className="pt-20 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Button 
              variant="ghost" 
              className="mb-6"
              onClick={() => setSelectedArticle(null)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à la documentation
            </Button>
            
            <div className="mb-6">
              <Badge variant="secondary" className="mb-2">
                {articleContents[selectedArticle]?.category}
              </Badge>
              <h1 className="text-3xl font-heading font-bold mb-2">
                {articleContents[selectedArticle]?.title}
              </h1>
              <p className="text-muted-foreground flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Temps de lecture : {articleContents[selectedArticle]?.duration}
              </p>
            </div>
            
            <Card>
              <CardContent className="p-6 md:p-8 prose prose-slate max-w-none">
                {renderContent(articleContents[selectedArticle]?.content || '')}
              </CardContent>
            </Card>
            
            <div className="mt-8 p-6 bg-muted/50 rounded-xl text-center">
              <h3 className="font-semibold mb-2">Cet article vous a été utile ?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                N'hésitez pas à nous contacter si vous avez des questions
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" asChild className="rounded-full">
                  <Link to="/tickets">
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Ouvrir un ticket
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Hero */}
          <section className="pt-24 pb-12 px-4 bg-muted/30">
            <div className="container mx-auto max-w-4xl text-center">
              <Badge variant="secondary" className="mb-4">
                <BookOpen className="w-3.5 h-3.5 mr-1.5" />
                Documentation
              </Badge>
              <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
                Tutoriels & Documentation
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Tout ce dont vous avez besoin pour maîtriser ResaWork
              </p>
              
              {/* Search */}
              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Rechercher dans la documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 rounded-full"
                />
              </div>
            </div>
          </section>

          {/* Categories */}
          <section className="py-12 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="grid md:grid-cols-2 gap-6">
                {filteredCategories.map((category) => (
                  <Card key={category.id} className="card-hover">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <category.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{category.title}</CardTitle>
                          <CardDescription>{category.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {category.articles.map((article) => (
                          <li key={article.id}>
                            <button 
                              onClick={() => setSelectedArticle(article.id)}
                              className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-muted transition-colors group text-left"
                            >
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm group-hover:text-primary transition-colors">
                                  {article.title}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">{article.duration}</span>
                                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-12 px-4 bg-muted/30">
            <div className="container mx-auto max-w-4xl">
              <h2 className="text-2xl font-heading font-bold mb-8 text-center">Questions fréquentes</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <h3 className="font-semibold mb-2 flex items-start gap-2">
                        <HelpCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        {faq.q}
                      </h3>
                      <p className="text-muted-foreground text-sm ml-7">{faq.a}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-12 px-4">
            <div className="container mx-auto max-w-4xl text-center">
              <h2 className="text-2xl font-heading font-bold mb-4">
                Besoin d'aide supplémentaire ?
              </h2>
              <p className="text-muted-foreground mb-6">
                Notre équipe est là pour vous aider
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Button variant="outline" asChild className="rounded-full">
                  <Link to="/tickets">
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Ouvrir un ticket
                  </Link>
                </Button>
                <Button asChild className="rounded-full">
                  <a href="mailto:support@resawork.fr">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Nous contacter
                  </a>
                </Button>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border">
        <div className="container mx-auto max-w-6xl text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} ResaWork par Agence MSC. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
}
